import { useState } from 'react';
import styled from 'styled-components';

import { addDataEvent, addErrorEvent, addInteractionEvent } from '@creditornot/browser-telemetry';
import { InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { WoltWarehouseItem } from 'common/api-types/warehouse';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { Form } from 'common/components/cookbook/Form';
import { FormField } from 'common/components/cookbook/FormField';
import { SubmitStateModal } from 'common/components/cookbook/Modals/SubmitStateModal';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { getErrorStatusCode, parseError } from 'common/utils/errorUtils';
import { WarehouseApiErrorNotification } from 'features/warehouse/common/components/WarehouseApiErrorNotification';

import { ButtonRow } from './ButtonRow';
import QuantityField from './QuantityField';
import { ReferenceIdField } from './ReferenceIdField';
import { ReferenceTypeField } from './ReferenceTypeField';
import { FormValues } from './types';
import { useCreateGoodsReceiptMutation } from '../../mutation';

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormSection = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
`;

const FormTitleRow = styled.div`
  margin-bottom: 24px;
`;

const InputRow = styled.div`
  display: flex;
  & + & {
    margin-top: 8px;
  }
`;

const StyledInputContainer = styled(InputContainer)`
  flex: 1;
`;

type Props = {
  selectedWarehouseId: string;
  selectedItem: WoltWarehouseItem;
  inboundLoadId?: string;
  poPublicId?: string;
  expectedQuantity?: number;
  onClose: (preserveQueryParams?: boolean) => void;
};

export const AddToInventoryForm: React.FC<Props> = ({
  selectedWarehouseId,
  selectedItem,
  inboundLoadId,
  poPublicId,
  expectedQuantity,
  onClose,
}) => {
  const formatMessage = useFormatMessage();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addSnackbar } = useSnackbar();

  const addClickEvent = (clickTarget: 'unit_quantity' | 'add_to_location') => {
    addInteractionEvent('add_to_inventory', 'click', {
      wm_venue_id: selectedWarehouseId,
      item_id: selectedItem.id,
      gtin: selectedItem.gtin,
      purchase_order_public_id: poPublicId,
      click_target: clickTarget,
    });
  };

  const { mutate, isPending, error, isSuccess } = useCreateGoodsReceiptMutation(
    selectedWarehouseId,
    selectedItem.id,
    {
      onMutate: () => {
        addDataEvent('add_to_inventory', 'receiving_loading', {
          wm_venue_id: selectedWarehouseId,
          item_id: selectedItem.id,
          gtin: selectedItem.gtin,
        });
      },
      onError: async (error) => {
        const errorMessage = await parseError(error);
        const statusCode = getErrorStatusCode(error);

        addErrorEvent('add_to_inventory', 'receiving_error', {
          error_message: errorMessage,
          error_code: statusCode,
          wm_venue_id: selectedWarehouseId,
          item_id: selectedItem.id,
          gtin: selectedItem.gtin,
        });
        setIsSubmitting(false);
      },
      onSuccess: (_data, variables) => {
        const { quantity } = variables;

        setTimeout(() => onClose(), 700);

        addSnackbar({
          message: formatMessage('receiving.receiving_success_snackbar', {
            quantity,
            name: selectedItem.name,
            measurementUnit: formatMessage('warehouse.measurement_unit:UNITS', {
              count: quantity,
            }),
            inventoryQuantity: (selectedItem.inventoryQuantity ?? 0) + quantity,
          }),
        });

        addDataEvent('add_to_inventory', 'receiving_success', {
          wm_venue_id: selectedWarehouseId,
          item_id: selectedItem.id,
          gtin: selectedItem.gtin,
        });
        setIsSubmitting(false);
      },
    },
  );

  const onSubmit = (formValues: FormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    const transformedValues = {
      inboundLoadId,
      referenceType: formValues.referenceType,
      referenceId: formValues.referenceId,
      quantity: parseInt(formValues.quantity),
      ...(formValues.locationName && { location: { name: formValues.locationName } }),
    };
    mutate(transformedValues);
  };

  const defaultLocation = selectedItem.location?.name;

  return (
    <Root>
      <Form
        onSubmit={onSubmit}
        onKeyDown={(e) => {
          if (e.code === 'Enter' && e.cancelable) e.preventDefault();
        }}
        defaultValues={{
          quantity: '',
          locationName: defaultLocation,
        }}
      >
        {!inboundLoadId && (
          <FormSection borderBottom={true}>
            <FormTitleRow>
              <T.subheadline>{formatMessage('receiving.shipment_information')}</T.subheadline>
            </FormTitleRow>
            <ReferenceTypeField />
            <ReferenceIdField />
          </FormSection>
        )}

        <FormSection>
          <FormTitleRow>
            <T.subheadline>{formatMessage('receiving.add_units_to_inventory')}</T.subheadline>
          </FormTitleRow>
          <InputRow>
            <StyledInputContainer
              style={{ marginRight: '16px' }}
              label={formatMessage('common.quantity')}
            >
              <QuantityField
                expectedQuantity={expectedQuantity}
                onClick={() => addClickEvent('unit_quantity')}
              />
            </StyledInputContainer>
            <StyledInputContainer label={formatMessage('common.location')}>
              <FormField
                name="locationName"
                renderInput={(props) => (
                  <Input
                    {...props}
                    type="text"
                    data-test-id="item-location"
                    onClick={() => addClickEvent('add_to_location')}
                  />
                )}
              />
            </StyledInputContainer>
          </InputRow>
        </FormSection>

        {error && (
          <ContentContainer verticalPadding="sm">
            <WarehouseApiErrorNotification error={error} />
          </ContentContainer>
        )}

        <ButtonRow
          selectedItem={selectedItem}
          onClose={onClose}
          selectedWarehouseId={selectedWarehouseId}
          isSubmitting={isSubmitting || isPending}
        />

        <SubmitStateModal
          isLoading={isPending}
          isSuccess={isSuccess}
          loadingText={formatMessage('receiving.adding_items')}
        />
      </Form>
    </Root>
  );
};
